// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsLinkRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import * as ThemePreferenceCommands from '@app/features/theme/commands/ThemePreferenceCommands';
import GlassTheme, {GlassTones} from '@app/features/theme/state/GlassTheme';
import HighlightPreference, {
	type HighlightColor,
	HighlightColors,
	highlightContrast,
} from '@app/features/theme/state/HighlightPreference';
import Theme from '@app/features/theme/state/Theme';
import {normalizeTheme} from '@app/features/theme/utils/NormalizeTheme';
import * as ThemeStudioCommands from '@app/features/theme_studio/commands/ThemeStudioCommands';
import {Button} from '@app/features/ui/button/Button';
import {CheckIcon, EyedropperIcon, PaintBrushBroadIcon} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {ChoiceCard, choiceCardStyles} from '@app/features/user/components/modals/tabs/appearance_tab/cards/ChoiceCard';
import {ThemePreviewArt} from '@app/features/user/components/modals/tabs/appearance_tab/cards/ThemePreviewArt';
import type {ThemeType} from '@konvo/constants/src/UserConstants';
import {ThemeTypes} from '@konvo/constants/src/UserConstants';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef} from 'react';

const THEME_DESCRIPTOR = msg({
	message: 'Theme',
	comment: 'Short label in the theme tab content. Keep it concise.',
});
const LIGHT_CARD_DESCRIPTOR = msg({message: 'Light', comment: 'Theme card name in Appearance. Keep it concise.'});
const LIGHT_CARD_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Clean and bright',
	comment: 'Short description under the Light theme card in Appearance.',
});
const GLASS_CARD_DESCRIPTOR = msg({message: 'Glass', comment: 'Theme card name in Appearance. Keep it concise.'});
const GLASS_CARD_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Frosted, over a wallpaper',
	comment: 'Short description under the Glass theme card in Appearance.',
});
const DARK_CARD_DESCRIPTOR = msg({message: 'Dark', comment: 'Theme card name in Appearance. Keep it concise.'});
const DARK_CARD_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Graphite, easy at night',
	comment: 'Short description under the Dark theme card in Appearance.',
});
const SYSTEM_CARD_DESCRIPTOR = msg({message: 'System', comment: 'Theme card name in Appearance. Keep it concise.'});
const SYSTEM_CARD_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Follows your computer',
	comment: 'Short description under the System theme card in Appearance.',
});
// Phone cards name the look in one word, like the concept names (Paper, Graphite, Mist).
const LIGHT_CARD_SHORT_DESCRIPTOR = msg({
	message: 'Paper',
	comment: 'One-word look name beside the Light theme card on the phone. Keep it to one word.',
});
const DARK_CARD_SHORT_DESCRIPTOR = msg({
	message: 'Graphite',
	comment: 'One-word look name beside the Dark theme card on the phone. Keep it to one word.',
});
const GLASS_LIGHT_CARD_SHORT_DESCRIPTOR = msg({
	message: 'Mist',
	comment: 'One-word look name beside the Glass theme card on the phone when glass is light. Keep it to one word.',
});
const GLASS_DARK_CARD_SHORT_DESCRIPTOR = msg({
	message: 'Smoked',
	comment: 'One-word look name beside the Glass theme card on the phone when glass is dark. Keep it to one word.',
});
const SYSTEM_CARD_SHORT_DESCRIPTOR = msg({
	message: 'Light or dark',
	comment: 'Short note beside the System theme card on the phone: follows the device between light and dark.',
});
const HIGHLIGHT_COLOUR_DESCRIPTOR = msg({
	message: 'Highlight colour',
	comment: 'Heading for the colour used by counts, unread dots and the new-messages line. Keep it concise.',
});
const HIGHLIGHT_INK_DESCRIPTOR = msg({
	message: 'Ink',
	comment: 'Highlight colour option: near-black in light themes, near-white in dark.',
});
const HIGHLIGHT_VIOLET_DESCRIPTOR = msg({message: 'Violet', comment: 'Highlight colour option.'});
const HIGHLIGHT_BLUE_DESCRIPTOR = msg({message: 'Blue', comment: 'Highlight colour option.'});
const HIGHLIGHT_TEAL_DESCRIPTOR = msg({message: 'Teal', comment: 'Highlight colour option.'});
const HIGHLIGHT_GREY_DESCRIPTOR = msg({message: 'Grey', comment: 'Highlight colour option.'});
const HIGHLIGHT_ORANGE_DESCRIPTOR = msg({
	message: 'Orange',
	comment: 'Highlight colour option: the apricot Konvo used before.',
});
const HIGHLIGHT_CUSTOM_DESCRIPTOR = msg({
	message: 'Custom colour',
	comment: 'Highlight colour option that opens a colour picker.',
});
const OPEN_THEME_STUDIO_DESCRIPTOR = msg({
	message: 'Open theme studio…',
	comment: 'Button or menu action label in the theme tab content. Keep it concise.',
});

interface ThemeSelectorProps {
	value: ThemeType;
	onChange: (theme: ThemeType) => void;
	disabled?: boolean;
	className?: string;
	ariaLabel?: string;
}

/** KONVO: the four themes as large preview cards (control kit, 2026-09). */
export const ThemeSelector: React.FC<ThemeSelectorProps> = observer(
	({value, onChange, disabled = false, className, ariaLabel}) => {
		const {i18n} = useLingui();
		const currentSelectedTheme: ThemeType = normalizeTheme(value);
		const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
		const themeToFocusRef = useRef<string | null>(null);
		const glassWallpaper = GlassTheme.wallpaperColors;
		const glassDark =
			GlassTheme.tone === GlassTones.DARK || (GlassTheme.tone === GlassTones.SYSTEM && Theme.systemPrefersDark);
		const handleThemeChange = useCallback(
			(newTheme: ThemeType) => {
				if (newTheme === currentSelectedTheme || disabled) return;
				themeToFocusRef.current = newTheme;
				onChange(newTheme);
			},
			[currentSelectedTheme, disabled, onChange],
		);
		const compact = MobileLayout.enabled;
		const themeOptions = useMemo(() => {
			const light = {
				type: ThemeTypes.LIGHT,
				name: i18n._(LIGHT_CARD_DESCRIPTOR),
				description: i18n._(compact ? LIGHT_CARD_SHORT_DESCRIPTOR : LIGHT_CARD_DESCRIPTION_DESCRIPTOR),
				preview: <ThemePreviewArt variant="paper" />,
			};
			const glass = {
				type: ThemeTypes.GLASS,
				name: i18n._(GLASS_CARD_DESCRIPTOR),
				description: i18n._(
					compact
						? glassDark
							? GLASS_DARK_CARD_SHORT_DESCRIPTOR
							: GLASS_LIGHT_CARD_SHORT_DESCRIPTOR
						: GLASS_CARD_DESCRIPTION_DESCRIPTOR,
				),
				preview: <ThemePreviewArt variant={glassDark ? 'glassDark' : 'glassLight'} wallpaper={glassWallpaper} />,
			};
			const dark = {
				type: ThemeTypes.DARK,
				name: i18n._(DARK_CARD_DESCRIPTOR),
				description: i18n._(compact ? DARK_CARD_SHORT_DESCRIPTOR : DARK_CARD_DESCRIPTION_DESCRIPTOR),
				preview: <ThemePreviewArt variant="graphite" />,
			};
			const system = {
				type: ThemeTypes.SYSTEM,
				name: i18n._(SYSTEM_CARD_DESCRIPTOR),
				description: i18n._(compact ? SYSTEM_CARD_SHORT_DESCRIPTOR : SYSTEM_CARD_DESCRIPTION_DESCRIPTOR),
				preview: <ThemePreviewArt variant="system" />,
			};
			// The phone's 2x2 grid reads light and dark across the top, glass and system below.
			return (compact ? [light, dark, glass, system] : [light, glass, dark, system]) satisfies ReadonlyArray<{
				type: ThemeType;
				name: string;
				description: string;
				preview: React.ReactNode;
			}>;
		}, [compact, glassDark, glassWallpaper, i18n.locale]);
		const handleKeyDown = useCallback(
			(event: React.KeyboardEvent, targetTheme: ThemeType) => {
				if (disabled) return;
				if (isKeyboardActivationKey(event.key)) {
					event.preventDefault();
					handleThemeChange(targetTheme);
				} else if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
					event.preventDefault();
					const order = themeOptions.map((option) => option.type);
					const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
					const currentIndex = Math.max(order.indexOf(currentSelectedTheme as (typeof order)[number]), 0);
					const nextTheme = order[(currentIndex + direction + order.length) % order.length];
					if (nextTheme) handleThemeChange(nextTheme);
				}
			},
			[themeOptions, currentSelectedTheme, disabled, handleThemeChange],
		);
		useEffect(() => {
			if (themeToFocusRef.current) {
				buttonRefs.current[themeToFocusRef.current]?.focus();
				themeToFocusRef.current = null;
			}
		}, [currentSelectedTheme]);
		return (
			<div
				className={clsx(choiceCardStyles.grid, choiceCardStyles.themeGrid, className)}
				role="radiogroup"
				aria-label={ariaLabel ?? i18n._(THEME_DESCRIPTOR)}
				data-flx="user.appearance-tab.theme.theme-tab-content.theme-button-group"
			>
				{themeOptions.map((option) => (
					<ChoiceCard
						key={option.type}
						ref={(el) => {
							buttonRefs.current[option.type] = el;
						}}
						name={option.name}
						description={option.description}
						checked={currentSelectedTheme === option.type}
						disabled={disabled}
						preview={option.preview}
						onSelect={() => handleThemeChange(option.type)}
						onKeyDown={(event) => handleKeyDown(event, option.type)}
						data-flx={`user.appearance-tab.theme.theme-tab-content.theme-card.${option.type}`}
					/>
				))}
			</div>
		);
	},
);

const HIGHLIGHT_OPTIONS: ReadonlyArray<{
	value: HighlightColor;
	swatch: string;
	on: string;
	label: typeof HIGHLIGHT_INK_DESCRIPTOR;
}> = [
	{
		value: HighlightColors.INK,
		swatch: 'var(--text-primary)',
		on: 'var(--background-primary)',
		label: HIGHLIGHT_INK_DESCRIPTOR,
	},
	{value: HighlightColors.VIOLET, swatch: '#6d5ce6', on: '#fff', label: HIGHLIGHT_VIOLET_DESCRIPTOR},
	{value: HighlightColors.BLUE, swatch: '#2f6fe0', on: '#fff', label: HIGHLIGHT_BLUE_DESCRIPTOR},
	{value: HighlightColors.TEAL, swatch: '#0f8f86', on: '#fff', label: HIGHLIGHT_TEAL_DESCRIPTOR},
	{value: HighlightColors.GREY, swatch: '#6e727d', on: '#fff', label: HIGHLIGHT_GREY_DESCRIPTOR},
	{value: HighlightColors.ORANGE, swatch: 'hsl(24, 63%, 51%)', on: '#fff', label: HIGHLIGHT_ORANGE_DESCRIPTOR},
];

/** KONVO: counts, unread dots and the new-messages line. Ink by default; kept per device. */
export const HighlightColorPicker: React.FC<{showHeading?: boolean}> = observer(({showHeading = true}) => {
	const {i18n} = useLingui();
	const customInputRef = useRef<HTMLInputElement>(null);
	const groupRef = useRef<HTMLDivElement>(null);
	// Radio-group keys: arrows move the choice (and focus); only the chosen swatch is a tab stop.
	const order: ReadonlyArray<HighlightColor> = [
		...HIGHLIGHT_OPTIONS.map((option) => option.value),
		HighlightColors.CUSTOM,
	];
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		const step = {ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1}[event.key];
		if (!step) return;
		event.preventDefault();
		const index = Math.max(order.indexOf(HighlightPreference.color), 0);
		const next = order[(index + step + order.length) % order.length]!;
		HighlightPreference.setColor(next);
		groupRef.current?.querySelector<HTMLElement>(`[data-flx="user.appearance-tab.theme.highlight.${next}"]`)?.focus();
	};
	return (
		<div data-flx="user.appearance-tab.theme.highlight">
			{showHeading && <p className={choiceCardStyles.subheading}>{i18n._(HIGHLIGHT_COLOUR_DESCRIPTOR)}</p>}
			<div
				ref={groupRef}
				className={choiceCardStyles.swatches}
				role="radiogroup"
				aria-label={i18n._(HIGHLIGHT_COLOUR_DESCRIPTOR)}
				onKeyDown={handleKeyDown}
			>
				{HIGHLIGHT_OPTIONS.map((option) => {
					const checked = HighlightPreference.color === option.value;
					return (
						<Tooltip key={option.value} text={i18n._(option.label)} position="top" delay={200}>
							<button
								type="button"
								role="radio"
								aria-checked={checked}
								tabIndex={checked ? 0 : -1}
								aria-label={i18n._(option.label)}
								className={choiceCardStyles.swatch}
								style={{background: option.swatch, ['--swatch-on' as string]: option.on}}
								onClick={() => HighlightPreference.setColor(option.value)}
								data-flx={`user.appearance-tab.theme.highlight.${option.value}`}
							>
								{checked && <CheckIcon size={18} weight="bold" />}
							</button>
						</Tooltip>
					);
				})}
				<Tooltip text={i18n._(HIGHLIGHT_CUSTOM_DESCRIPTOR)} position="top" delay={200}>
					<button
						type="button"
						role="radio"
						aria-checked={HighlightPreference.color === HighlightColors.CUSTOM}
						tabIndex={HighlightPreference.color === HighlightColors.CUSTOM ? 0 : -1}
						aria-label={i18n._(HIGHLIGHT_CUSTOM_DESCRIPTOR)}
						className={clsx(choiceCardStyles.swatch, choiceCardStyles.customSwatch)}
						style={{
							background: HighlightPreference.customColor,
							['--swatch-on' as string]: highlightContrast(HighlightPreference.customColor),
						}}
						onClick={() => {
							HighlightPreference.setColor(HighlightColors.CUSTOM);
							customInputRef.current?.click();
						}}
						data-flx="user.appearance-tab.theme.highlight.custom"
					>
						{HighlightPreference.color === HighlightColors.CUSTOM ? (
							<CheckIcon size={18} weight="bold" />
						) : (
							<EyedropperIcon size={18} />
						)}
					</button>
				</Tooltip>
				<input
					ref={customInputRef}
					type="color"
					className={choiceCardStyles.hiddenColorInput}
					value={HighlightPreference.customColor}
					tabIndex={-1}
					aria-hidden="true"
					onChange={(event) => HighlightPreference.setCustomColor(event.target.value)}
				/>
			</div>
		</div>
	);
});

export const ThemeTabContent: React.FC = observer(() => {
	const {i18n} = useLingui();
	const themePreference = Theme.themePreference;
	const currentSelectedTheme: ThemeType = themePreference;
	const handleThemeChange = useCallback(
		(newTheme: ThemeType) => {
			if (newTheme === currentSelectedTheme) return;
			ThemePreferenceCommands.updateThemePreference(newTheme);
		},
		[currentSelectedTheme],
	);
	const handleOpenStudio = useCallback(() => {
		ThemeStudioCommands.openThemeStudio();
	}, []);
	const isMobile = MobileLayout.enabled;
	return (
		<>
			<ThemeSelector
				value={currentSelectedTheme}
				onChange={handleThemeChange}
				data-flx="user.appearance-tab.theme.theme-tab-content.theme-selector.theme-change"
			/>
			{/* The phone gives the highlight colour its own card (AppearanceTab). */}
			{isMobile ? null : <HighlightColorPicker />}
			{isMobile ? (
				<SettingsLinkRow
					title={i18n._(OPEN_THEME_STUDIO_DESCRIPTOR)}
					onClick={handleOpenStudio}
					data-flx="user.appearance-tab.theme.theme-tab-content.link-row.open-studio"
				/>
			) : (
				<Button
					variant="secondary"
					fitContent
					leftIcon={
						<PaintBrushBroadIcon
							size={18}
							data-flx="user.appearance-tab.theme.theme-tab-content.paint-brush-broad-icon"
						/>
					}
					onClick={handleOpenStudio}
					data-flx="user.appearance-tab.theme.theme-tab-content.button.open-studio"
				>
					{i18n._(OPEN_THEME_STUDIO_DESCRIPTOR)}
				</Button>
			)}
		</>
	);
});
